import { ComponentProps, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import useUser from '@alltrails/context/hooks/useUser';
import NoSsrSuspense from '@alltrails/core/components/NoSsrSuspense';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { useModal } from '@alltrails/modal';
import type Context from '@alltrails/types/Context';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import type { Visibility } from '@alltrails/types/Visibility';
import { type ListsState, setListsAndListItems } from '../slices/lists';
import ListItem from '../types/ListItem';
import type { AnalyticsInfo } from '../utils/listAmplitudeHelpers';
import { isCompleted, isSaved, isVerified as isVerifiedUtil } from '../utils/listUtils';
import { useAppDispatch, useAppSelector } from '../utils/redux';
import getListsAndListItems from '../utils/requests';

const ReduxSaveToListModal = lazy(() => import('../components/ReduxSaveToListModal'));
const SignUpModal = lazy(() => import('@alltrails/user-auth/components/SignUpModal'));

type ItemType = ListItem['type'];

type SignUpInfo = {
  onSuccess?: () => void;
};

/**
 * @param signUpInfo
 * @param preventAutoInitialize boolean to prevent list fetching on successful sign in. NOTE: setting `true` may require you to manually initialize lists.
 * @param analyticsInfo additional analytics info to be passed to the modal
 * @returns
 */
const useReduxListItems = (signUpInfo?: SignUpInfo, preventAutoInitialize?: boolean, analyticsInfo?: AnalyticsInfo) => {
  const dispatch = useAppDispatch();
  const currentUser = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const { listItems } = useAppSelector((state: { lists: ListsState; context: Context }) => ({
    listItems: state.lists.listItems,
    currentUser: state.context.currentUser,
    languageRegionCode: state.context.languageRegionCode
  }));
  const [{ id, type, contentPrivacy, isCustomRoute }, setIdTypeAndPrivacy] = useState<{
    id: number;
    type: ItemType;
    contentPrivacy?: Visibility;
    isCustomRoute?: boolean;
  }>({
    id: 0,
    type: '' as ItemType,
    contentPrivacy: '' as Visibility,
    isCustomRoute: false
  });
  const [shouldOpenListModal, setShouldOpenListModal] = useState(false);

  const { modal, openModal } = useModal(
    ReduxSaveToListModal,
    useMemo(() => ({ id, type, contentPrivacy, analyticsInfo, isCustomRoute }), [id, type, contentPrivacy, analyticsInfo, isCustomRoute])
  );

  const onSuccess = useCallback(async () => {
    if (preventAutoInitialize) {
      setShouldOpenListModal(true);
    } else {
      const payload = await getListsAndListItems();
      dispatch(setListsAndListItems(payload));
      openModal();
    }

    signUpInfo?.onSuccess?.();
  }, [dispatch, openModal, preventAutoInitialize, signUpInfo]);

  const deeplinkData = useMemo<DeeplinkData | undefined>(() => {
    if (type === 'trail' && id) {
      return { pageId: id, pageType: 'trail' };
    }
    return undefined;
  }, [id, type]);

  const { modal: signUpModal, openModal: openSignupModal } = useModal(
    SignUpModal,
    useMemo<Omit<ComponentProps<typeof SignUpModal>, 'closeModal'>>(
      () => ({
        autoClose: true,
        deeplinkData,
        isOpen: true,
        languageRegionCode,
        onSuccess,
        trigger: 'Favorite',
        upsellTrigger: 'Favorite'
      }),
      [deeplinkData, languageRegionCode, onSuccess]
    )
  );

  const handleFavoriteClick = useCallback(
    (itemType: ItemType, itemId: number, itemPrivacy?: Visibility, isCustomRoute?: boolean) => {
      // Update state before opening the signup modal so that if signup is successful we can automatically open the save to list modal
      setIdTypeAndPrivacy({ id: itemId, type: itemType, contentPrivacy: itemPrivacy, isCustomRoute });

      if (!currentUser) {
        openSignupModal();
        return;
      }

      openModal();
    },
    [currentUser, openModal, openSignupModal]
  );

  useEffect(() => {
    if (shouldOpenListModal && !!listItems) {
      openModal();
      setShouldOpenListModal(false);
    }
  }, [listItems, openModal, shouldOpenListModal]);

  const isFavorite = useCallback((itemType: string, itemId: number) => isSaved(listItems, itemType, itemId), [listItems]);
  const isComplete = useCallback((itemType: string, itemId: number) => isCompleted(listItems, itemType, itemId), [listItems]);
  const isVerified = useCallback((itemType: string, itemId: number) => isVerifiedUtil(listItems, itemType, itemId), [listItems]);
  return {
    isFavorite,
    isComplete,
    isVerified,
    handleFavoriteClick,
    listModal: <NoSsrSuspense fallback={null}>{modal}</NoSsrSuspense>,
    signUpModal: <NoSsrSuspense fallback={null}>{signUpModal}</NoSsrSuspense>
  };
};

export default useReduxListItems;
